/**
 * ProblemTypesList — the flat entry point into the problem-type graph. Lists every problem
 * type for a subject (label, band, size, review status), filterable, and links each into
 * the graph traverser. See docs/FeatureDesigns/problem-type-similarity.md.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';

const SUBJECTS = ['physics', 'chemistry', 'maths'];
const bandColor = (b?: string): string =>
  /easy/i.test(b || '') ? '#15803d' : /hard/i.test(b || '') ? '#b91c1c' : /med/i.test(b || '') ? '#b45309' : 'var(--text-secondary)';

export function ProblemTypesList() {
  const [params, setParams] = useSearchParams();
  const subject = params.get('subject') || 'physics';
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [band, setBand] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['problem-types', subject],
    queryFn: () => adminApi.listProblemTypes(subject),
    enabled: !!subject,
  });

  const rows = useMemo(() => {
    let list = data?.problemTypes ?? [];
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter(t => (t.label ?? t.id).toLowerCase().includes(s)); }
    if (band) list = list.filter(t => (t.difficultyBand ?? '').toLowerCase() === band);
    return [...list].sort((a, b) => (b.size ?? 0) - (a.size ?? 0));
  }, [data, q, band]);

  const setSubject = (s: string) => setParams({ subject: s });

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Problem types</h1>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Equivalence classes of interchangeable questions (same concept · band · method). Click one to explore the graph.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={subject} onChange={e => setSubject(e.target.value)} style={sel}>
          {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by label…" style={{ ...sel, flex: 1, minWidth: 180 }} />
        <select value={band} onChange={e => setBand(e.target.value)} style={sel}>
          <option value="">all bands</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{rows.length} types</span>
      </div>

      {isLoading ? <div style={{ color: 'var(--text-secondary)' }}>Loading…</div> : (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden' }}>
          {rows.map((t, i) => (
            <button
              key={t.id}
              onClick={() => navigate(`/problem-types/${t.id}?subject=${subject}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', borderTop: i ? '1px solid var(--border-color)' : 'none',
                background: 'transparent', cursor: 'pointer', fontSize: 13,
              }}
            >
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: bandColor(t.difficultyBand), flex: 'none' }} />
              <span style={{ color: 'var(--text-primary)', flex: 1 }}>{t.label ?? t.id}</span>
              {t.reviewStatus && t.reviewStatus !== 'auto' && (
                <span style={{ fontSize: 10, color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '0 5px' }}>{t.reviewStatus}</span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{t.size}q</span>
              <span style={{ color: 'var(--text-secondary)' }}>›</span>
            </button>
          ))}
          {!rows.length && <div style={{ padding: 16, color: 'var(--text-secondary)' }}>No problem types match.</div>}
        </div>
      )}
    </div>
  );
}

const sel: React.CSSProperties = {
  padding: '5px 8px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, background: 'var(--surface, #fff)', color: 'var(--text-primary)',
};
